/**
 * The pieces the Assets page and the control both draw: tiles of true shapes, the folder rail, the path, the fit
 * bar, a file's stage and facts. Ported from the confirmed prototype's markup and class names.
 */
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { LibraryAsset, LibraryFolder, LibraryUse } from "../../shared/types";
import { BrandGlyph } from "../components/BrandMark";
import { Icon } from "../components/ui/Icon";
import type { IconName } from "../components/ui/icon-set";
import {
  ALL,
  agoLabel,
  aspectOf,
  bytesLabel,
  childFolders,
  countIn,
  fitsIn,
  listedLine,
  extOf,
  fits,
  folderMatches,
  folderName,
  folderPath,
  groupFolders,
  groupKids,
  groupOfAsset,
  groupOfFolder,
  justifyRows,
  searchPath,
  kindName,
  lenLabel,
  type ListContext,
  TRASH_NOTE,
  trashFolderName,
  trashLine,
  usedLine,
} from "./library";
import type { GroupKey } from "./fold";
import { useRailFold } from "./useRailFold";
import { type Upload, uploadsAsAssets } from "./store";

/** Flashkut's mark, the coral square. */
export function FkMark({ className = "" }: { className?: string }) {
  return (
    <svg className={`fkm ${className}`.trim()} viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" rx="9" fill="#F97066" />
      <circle cx="16" cy="16" r="5.1" fill="#fff" />
    </svg>
  );
}

function wave(id: string, n = 28): number[] {
  let s = 0;
  for (const ch of id) s = (s * 31 + ch.charCodeAt(0)) % 997;
  return Array.from({ length: n }, (_, i) => 22 + Math.round(Math.abs(Math.sin(i * 1.3 + s) * 48 + Math.sin(i * 0.45 + s / 7) * 22)));
}

/** A file with no picture of its own: a page for a document, a wave for a sound. */
function Art({ a }: { a: LibraryAsset }) {
  if (a.kind === "pdf" || a.kind === "doc") {
    return (
      <span className="as-art as-a-doc">
        <span className="pg">
          <i />
          <i />
          <i />
          <i className="s" />
          <em className={a.kind}>{a.kind === "pdf" ? "PDF" : extOf(a.name) || "TXT"}</em>
        </span>
      </span>
    );
  }
  if (a.kind === "sound") {
    return (
      <span className="as-art as-a-wave">
        {wave(a.id).map((h, i) => (
          <i key={i} style={{ height: `${h}%` }} />
        ))}
      </span>
    );
  }
  return (
    <span className="as-art as-a-blank">
      <Icon name={a.kind === "video" ? "video" : "pic"} />
    </span>
  );
}

/** How far past the scroll area's edge a video's frame is still drawn, so it is ready before it scrolls in. */
const NEAR_PX = 600;

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(p).overflowY)) return p;
  }
  return null;
}

/** True while the element is within NEAR_PX of the scroll area it sits in (or of the window). */
function useNear(ref: React.RefObject<HTMLElement | null>): boolean {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const watch = new IntersectionObserver((seen) => setNear(seen[seen.length - 1].isIntersecting), {
      root: scrollParent(el),
      rootMargin: `${NEAR_PX}px 0px`,
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, [ref]);
  return near;
}

/**
 * A video with no cover picture shows its first frame, which asks the browser to open the file. Hundreds of those
 * at once froze the tab, so the video exists only while its tile is near the view.
 */
function FirstFrame({ a }: { a: LibraryAsset }) {
  const ref = useRef<HTMLSpanElement>(null);
  const near = useNear(ref);
  // Why the stand-in stays under the video: it is what the observer watches, so the video goes again when the tile is far.
  return (
    <>
      <span ref={ref} className="as-art as-a-blank">
        <Icon name="video" />
      </span>
      {near ? <video src={`${a.url}#t=0.5`} muted playsInline preload="metadata" /> : null}
    </>
  );
}

/** What a tile shows: the picture, the video's first frame or poster, or the file's art. */
export function Media({ a }: { a: LibraryAsset }) {
  if (a.kind === "image" && a.url) return <img src={a.url} alt="" draggable={false} loading="lazy" decoding="async" />;
  if (a.kind === "video") {
    if (a.poster_url) return <img src={a.poster_url} alt="" draggable={false} loading="lazy" decoding="async" />;
    // Why the video itself: the library makes no poster, so the tile draws the first frame, as Flashkut's tiles do.
    if (a.url && a.source !== "room") return <FirstFrame a={a} />;
  }
  return <Art a={a} />;
}

/** A small square of a file, for the footer and lists. */
export function Thumb({ a }: { a: LibraryAsset }) {
  if ((a.kind === "image" && a.url) || (a.kind === "video" && (a.poster_url || a.url))) return <Media a={a} />;
  return (
    <span className={`as-ti ${a.kind}`}>
      {a.kind === "pdf" || a.kind === "doc" ? <b>{a.kind === "pdf" ? "PDF" : extOf(a.name) || "TXT"}</b> : <Icon name={a.kind === "sound" ? "music" : a.kind === "video" ? "video" : "pic"} />}
    </span>
  );
}

const STAGES = (folder: string) => ["Uploading", "Checking it", `Putting it in ${folder}`, "Ready"];

/** What a file going up says: its percent while it goes, then the step it is on. */
export function upLine(u: Upload, folder: string): string {
  if (u.error) return u.error;
  return u.stage === 0 ? `Uploading ${Math.round(u.pct)}%` : STAGES(folder)[u.stage];
}

export interface UpSummary {
  /** Across every file still going, by size: one file is its own percent. */
  pct: number;
  /** The Upload button's words: the percent, then the step the slowest file is on. */
  label: string;
  /** The footer's words for the same. */
  short: string;
  /** The file that went first. */
  first: Upload;
  more: number;
}

/** One reading of all the files going up, or null when none is (a failed file is not going). */
export function upSummary(uploads: Upload[], folder: string): UpSummary | null {
  const going = uploads.filter((u) => !u.error);
  if (!going.length) return null;
  const done = (u: Upload) => (u.stage === 0 ? u.pct : 100);
  const total = going.reduce((n, u) => n + u.size, 0);
  const sum = going.reduce((n, u) => n + done(u) * (total ? u.size : 1), 0);
  const pct = Math.round(sum / (total || going.length));
  const behind = Math.min(...going.map((u) => u.stage));
  const step = STAGES(folder)[behind];
  return { pct, label: behind === 0 ? `Uploading ${pct}%` : step, short: behind === 0 ? `${pct}%` : step, first: going[going.length - 1], more: going.length - 1 };
}

/** The footer while files go up: the first one's picture and name, how far, and that it is picked when in. */
export function UpFoot({ s }: { s: UpSummary }) {
  const [a] = uploadsAsAssets([s.first]);
  return (
    <span className="as-pf-l as-upf">
      <span className="as-pf-t">
        <Thumb a={a} />
      </span>
      <span className="sp">
        <span className="as-upf-n">
          <b>{s.first.name}</b>
          {s.more ? <em>and {s.more} more</em> : null}
          <i className="num">{s.short}</i>
        </span>
        <span className="as-upf-bar">
          <i style={{ width: `${s.pct}%` }} />
        </span>
        <span>It is picked for you when it is in.</span>
      </span>
    </span>
  );
}

function UpBars({ u, folder }: { u: Upload; folder: string }) {
  if (u.error) {
    return (
      <span className="as-upov">
        <b>{u.error}</b>
      </span>
    );
  }
  const fill = (i: number) => (i < u.stage ? 1 : i === u.stage ? (u.stage === 0 ? u.pct / 100 : 0.55) : 0);
  return (
    <span className="as-upov" role="status">
      <b>{u.stage === 0 ? `Uploading ${Math.round(u.pct)}%` : STAGES(folder)[u.stage]}</b>
      <span className="as-bars">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} style={{ ["--f" as string]: fill(i) }} />
        ))}
      </span>
    </span>
  );
}

interface GridProps {
  ctx: ListContext;
  folders: LibraryFolder[];
  files: LibraryAsset[];
  uploads: Upload[];
  sel: string | null;
  auto: string | null;
  fresh: string[];
  /** Tile height the rows aim for: 132 in the control, 164 on the page. */
  height: number;
  onSelect: (id: string) => void;
  onUse?: (id: string) => void;
  /** In Trash: every tile shows its tick, and a click ticks it. */
  trash?: ReadonlySet<string>;
  /** False for a viewer in Trash, who cannot restore: the tiles show no tick and a click does nothing. */
  ticks?: boolean;
}

function tileMeta(ctx: ListContext, folders: LibraryFolder[], a: LibraryAsset, fresh: boolean): ReactNode {
  if (!fits(ctx, a)) return "Doesn’t fit here";
  if (ctx.q.trim()) return searchPath(folders, a);
  if (fresh) {
    return (
      <>
        <Icon name="check" />
        Just added
      </>
    );
  }
  const used = usedLine(a.uses);
  if (a.source === "flashkut") {
    return (
      <>
        <span className="as-fkl">
          <FkMark />
          <span>
            <span className="as-mi">Made in </span>Flashkut
          </span>
        </span>
        {used === "Not used yet" ? null : (
          <span className="as-mu">
            <i className="as-dt">·</i>
            {used}
          </span>
        )}
      </>
    );
  }
  if (a.source === "room") {
    // A note or a guest's file has no room: it says who it went to or came from.
    if (!a.webinar_id && a.from_label) return a.from_label;
    return a.duration_sec ? `Replay · ${lenLabel(a.duration_sec)}` : "Replay";
  }
  return used;
}

/** The grid of true shapes: rows the full width at about the same height, re-laid when the width changes. */
function FileGrid({ ctx, folders, files, uploads, sel, auto, fresh, height, onSelect, onUse, trash, ticks = true }: GridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const ups = new Map(uploads.map((u) => [u.id, u]));
  const aspects = files.map((a) => aspectOf(a));
  const widths = justifyRows(aspects, width, height);
  return (
    <div className="as-grid" ref={ref}>
      {files.map((a, i) => {
        const up = ups.get(a.id);
        const on = trash ? ticks && trash.has(a.id) : sel === a.id;
        const ok = fits(ctx, a);
        const isFresh = fresh.includes(a.id);
        const cls = ["as-tile", on && "on", trash && "tk", !ok && "nofit", up && "up", up?.error && "upfail", isFresh && "fresh", auto === a.id && "auto"].filter(Boolean).join(" ");
        return (
          <div className={cls} key={a.id} style={{ width: `${widths[i]}px` }}>
            <button
              type="button"
              className="as-hit"
              aria-pressed={trash && !ticks ? undefined : on}
              disabled={!!up && !up.error}
              title={a.name}
              onClick={(e) => {
                if (trash && !ticks) return;
                if (on && e.detail >= 2 && onUse) onUse(a.id);
                else onSelect(a.id);
              }}
            >
              <span className="as-m" style={{ aspectRatio: aspects[i].toFixed(3) }}>
                <Media a={a} />
                {a.source === "flashkut" ? (
                  <span className="as-fkb" title="Made in Flashkut">
                    <FkMark />
                  </span>
                ) : null}
                {a.duration_sec != null && !up ? (
                  <span className="as-dur num">
                    {a.kind === "video" ? <Icon name="asplay" /> : null}
                    {lenLabel(a.duration_sec)}
                  </span>
                ) : null}
                {on && !trash ? (
                  <span className="as-ck">
                    <Icon name="check" />
                  </span>
                ) : null}
                {up ? <UpBars u={up} folder={folderName(folders, up.folderId)} /> : null}
              </span>
              <span className="as-cap" title={trash && a.uses.length ? `From ${trashFolderName(a, folders)}` : undefined}>
                <b>{a.name}</b>
                <span>{up ? (up.error ? "Didn’t finish" : "Uploading…") : trash ? trashLine(a, folders) : tileMeta(ctx, folders, a, isFresh)}</span>
              </span>
            </button>
            {trash && ticks ? (
              <button type="button" className={`as-ck tick${on ? "" : " off"}`} aria-pressed={on} aria-label={`Select ${a.name}`} onClick={() => onSelect(a.id)}>
                <Icon name="check" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function folderIconName(f: LibraryFolder): IconName {
  const map: Record<string, IconName> = { Clips: "video", Images: "pic", Handouts: "file", Brand: "palette" };
  return f.system ? "fold" : (map[f.name] ?? "fold");
}

function folderIcon(f: LibraryFolder): ReactNode {
  return <Icon name={folderIconName(f)} />;
}

function FolderCard({ f, count, inName, menu, onOpen }: { f: LibraryFolder; count: number; inName?: string; menu?: ReactNode; onOpen: () => void }) {
  const card = (
    <button type="button" className="as-fcard" onClick={onOpen}>
      <span className="as-fic">{folderIcon(f)}</span>
      <span className="sp">
        <b>{f.name}</b>
        <span>
          {count} file{count === 1 ? "" : "s"}
          {inName ? ` · in ${inName}` : ""}
        </span>
      </span>
    </button>
  );
  if (!menu) return card;
  // Why beside, not inside: a button cannot hold a button.
  return (
    <div className="as-fcardw">
      {card}
      {menu}
    </div>
  );
}

export function SearchBox({ id, value, placeholder, onChange, inputRef }: { id: string; value: string; placeholder: string; onChange: (v: string) => void; inputRef?: React.Ref<HTMLInputElement> }) {
  return (
    <label className="as-search" htmlFor={id}>
      <Icon name="search" />
      <input dir="auto" id={id} ref={inputRef} type="search" placeholder={placeholder} value={value} autoComplete="off" onChange={(e) => onChange(e.target.value)} />
      {value ? (
        <button type="button" className="as-qx" aria-label="Clear search" onClick={() => onChange("")}>
          <Icon name="x" />
        </button>
      ) : (
        <span className="as-kbd">/</span>
      )}
    </label>
  );
}

interface RailProps {
  ctx: ListContext;
  folders: LibraryFolder[];
  assets: LibraryAsset[];
  search?: ReactNode;
  /** False for a viewer, who can look but not add. */
  canAdd?: boolean;
  /** True while the library cannot be read: New folder stays but cannot be pressed, and no count is shown. */
  blind?: boolean;
  /** The Trash row, under All files: how many files are in it, and whether it is the one open. */
  trash?: { count: number; on: boolean; onOpen: () => void };
  onOpen: (id: string) => void;
  onNewFolder: () => void;
}

/** A product's mark at 14px: Flashkut's coral square (the Flashsuite menu's), Flashroom's door. */
export function GroupMark({ group }: { group: "Flashkut" | "Flashroom" }) {
  return group === "Flashkut" ? <FkMark className="as-gm" /> : <BrandGlyph size={14} className="as-gm" />;
}

/**
 * All files, Trash, then three groups that fold: FOLDERS (the person's own tree, whose branches fold too), FLASHKUT
 * (one folder per project) and FLASHROOM (one folder per recorded room). The product groups are read only: no menu, no
 * Move to, no upload.
 */
export function Rail({ ctx, folders, assets, search, canAdd = true, blind = false, trash, onOpen, onNewFolder }: RailProps) {
  const { fold, sizes, isOpen, toggle, toggleKid, own } = useRailFold(folders, assets, ctx.q, ctx.folder);
  const searching = !!ctx.q.trim();
  const count = (n: number) => (blind ? null : <b className="num">{n}</b>);
  const head = (g: GroupKey) => {
    const open = isOpen(g);
    return (
      <button type="button" className="as-rk as-gk" aria-expanded={open} onClick={() => toggle(g)}>
        <span className={`as-gtw${open ? " open" : ""}`}>
          <Icon name="chev" />
        </span>
        {g === "Folders" ? null : <GroupMark group={g} />}
        <span className="as-gn">{g}</span>
        {open || blind ? null : <b className="num as-gc">{sizes[g]}</b>}
      </button>
    );
  };
  const row = (f: LibraryFolder | null, lvl = 0, kids = 0) => {
    const id = f ? f.id : ALL;
    const on = !searching && ctx.folder === id;
    const n = countIn(folders, assets, id);
    const none = ctx.mode === "pick" && !ctx.all && !fitsIn(folders, assets, ctx, id);
    const open = !!f && fold.folders.includes(id);
    const button = (
      <button
        type="button"
        key={id}
        className={["as-rr", on && "on", none && "none", f && "tree"].filter(Boolean).join(" ")}
        style={lvl ? ({ ["--lvl" as string]: lvl } as React.CSSProperties) : undefined}
        aria-current={on ? "true" : undefined}
        onClick={() => onOpen(id)}
      >
        {f ? folderIcon(f) : <Icon name="allf" />}
        <span className="sp">{f ? f.name : "All files"}</span>
        {count(n)}
      </button>
    );
    if (!f) return button;
    return (
      <div className="as-rrw" key={id} style={{ ["--lvl" as string]: lvl } as React.CSSProperties}>
        {kids ? (
          <button type="button" className={`as-tw${open ? " open" : ""}`} aria-expanded={open} aria-label={`${open ? "Close" : "Open"} ${f.name}`} onClick={() => toggleKid(id)}>
            <Icon name="chev" />
          </button>
        ) : null}
        {button}
      </div>
    );
  };
  const tree = (parent: string | null, lvl: number): ReactNode[] =>
    own
      .filter((f) => f.parent_id === parent)
      .flatMap((f) => {
        const kids = own.filter((x) => x.parent_id === f.id).length;
        return [row(f, lvl, kids), ...(kids && fold.folders.includes(f.id) ? tree(f.id, lvl + 1) : [])];
      });
  const product = (g: "Flashkut" | "Flashroom") =>
    sizes[g] ? (
      <>
        {head(g)}
        {isOpen(g)
          ? groupFolders(folders, g).flatMap((f) => {
              const kids = groupKids(folders, f.id);
              return [row(f, 0, kids.length), ...(kids.length && fold.folders.includes(f.id) ? kids.map((k) => row(k, 1)) : [])];
            })
          : null}
      </>
    ) : null;
  return (
    <nav className="as-rail" aria-label="Folders">
      {search}
      {row(null)}
      {trash ? (
        <button type="button" className={["as-rr", trash.on && "on"].filter(Boolean).join(" ")} aria-current={trash.on ? "true" : undefined} onClick={trash.onOpen}>
          <Icon name="trash" />
          <span className="sp">Trash</span>
          {count(trash.count)}
        </button>
      ) : null}
      {head("Folders")}
      {isOpen("Folders") ? tree(null, 0) : null}
      {isOpen("Folders") && canAdd ? (
        <button type="button" className="as-rr as-rr-add" disabled={blind} onClick={onNewFolder}>
          <Icon name="plus" />
          <span className="sp">New folder</span>
        </button>
      ) : null}
      {product("Flashkut")}
      {product("Flashroom")}
    </nav>
  );
}

export function Crumb({ ctx, folders, menu, onOpen }: { ctx: ListContext; folders: LibraryFolder[]; menu?: ReactNode; onOpen: (id: string) => void }) {
  if (ctx.q.trim()) {
    return (
      <div className="as-crumb">
        <h2>Results for “{ctx.q.trim()}”</h2>
        <span className="as-ct num">in every folder</span>
      </div>
    );
  }
  if (ctx.folder === ALL) {
    return (
      <div className="as-crumb">
        <h2>All files</h2>
      </div>
    );
  }
  const open = folders.find((f) => f.id === ctx.folder);
  const group = open ? groupOfFolder(open) : null;
  const parent = open && group === "Flashroom" && open.parent_id ? folders.find((f) => f.id === open.parent_id) : null;
  if (open && group) {
    return (
      <nav className="as-crumb" aria-label="Path">
        <button type="button" className="as-cb" onClick={() => onOpen(ALL)}>
          Assets
        </button>
        <Icon name="chev" />
        <span className="as-cg">{group}</span>
        <Icon name="chev" />
        {parent ? (
          <>
            <button type="button" className="as-cb" onClick={() => onOpen(parent.id)}>
              {parent.name}
            </button>
            <Icon name="chev" />
          </>
        ) : null}
        <span className="as-cur">
          <h2>{open.name}</h2>
        </span>
      </nav>
    );
  }
  const path = folderPath(folders, ctx.folder);
  return (
    <nav className="as-crumb" aria-label="Path">
      <button type="button" className="as-cb" onClick={() => onOpen(ALL)}>
        Assets
      </button>
      {path.map((f, i) => (
        <Fragment key={f.id}>
          <Icon name="chev" />
          {i === path.length - 1 ? (
            <span className="as-cur">
              <h2>{f.name}</h2>
              {menu}
            </span>
          ) : (
            <button type="button" className="as-cb" onClick={() => onOpen(f.id)}>
              {f.name}
            </button>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

function NewFolderRow({ where, onCreate, onCancel }: { where: string; onCreate: (name: string) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [nudge, setNudge] = useState(false);
  const go = () => {
    if (!name.trim()) {
      setNudge(true);
      return;
    }
    onCreate(name.trim());
  };
  return (
    <div className="as-nf">
      <Icon name="foldplus" />
      <input dir="auto"
        className="inp"
        placeholder="Folder name"
        autoComplete="off"
        maxLength={40}
        autoFocus
        aria-invalid={nudge || undefined}
        aria-label="Folder name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setNudge(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            go();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }
        }}
      />
      {nudge ? (
        <span className="as-nf-w bad" role="alert">
          Give the folder a name first.
        </span>
      ) : (
        <span className="as-nf-w">In {where}</span>
      )}
      <button type="button" className="btn s p" onClick={go}>
        Create
      </button>
      <button type="button" className="btn s q" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function FitBar({ ctx, onToggle }: { ctx: ListContext; onToggle: () => void }) {
  if (ctx.mode !== "pick" || !ctx.fit.label) return null;
  return (
    <div className={`as-fit${ctx.all ? " off" : ""}`}>
      <Icon name="filter" />
      <span className="sp">
        {ctx.all ? (
          <>
            <b>Showing everything.</b> Only {ctx.fit.label} can be used here.
          </>
        ) : (
          <>
            <b>Only {ctx.fit.label}</b>
            {ctx.fit.hint ? ` · ${ctx.fit.hint}` : ""}
          </>
        )}
      </span>
      <button type="button" className="link" onClick={onToggle}>
        {ctx.all ? "Only what fits" : "Show everything"}
      </button>
    </div>
  );
}

interface EmptyProps {
  ctx: ListContext;
  folders: LibraryFolder[];
  assets: LibraryAsset[];
  onClear: () => void;
  onOpen: (id: string) => void;
  onAll: () => void;
  onUpload: () => void;
  canAdd?: boolean;
}

export function Empty({ ctx, folders, assets, onClear, onOpen, onAll, onUpload, canAdd = true }: EmptyProps) {
  if (ctx.q.trim()) {
    return (
      <div className="as-empty">
        <Icon name="search" />
        <b>Nothing matches “{ctx.q.trim()}”</b>
        <button type="button" className="btn s" onClick={onClear}>
          Clear search
        </button>
      </div>
    );
  }
  const f = folders.find((x) => x.id === ctx.folder) ?? null;
  const name = folderName(folders, ctx.folder === ALL ? null : ctx.folder);
  const here = assets.some((a) => ctx.folder === ALL || a.folder_id === ctx.folder);
  if (ctx.mode === "pick" && !ctx.all && here) {
    const best = folders.find((x) => x.id !== ctx.folder && fitsIn(folders, assets, ctx, x.id));
    return (
      <div className="as-empty">
        <Icon name="filter" />
        <b>
          No {ctx.fit.label} in {name}
        </b>
        <div className="row g8">
          {best ? (
            <button type="button" className="btn s" onClick={() => onOpen(best.id)}>
              Open {best.name}
            </button>
          ) : null}
          <button type="button" className="btn s q" onClick={onAll}>
            Show everything
          </button>
        </div>
      </div>
    );
  }
  if (f?.system) {
    return (
      <div className="as-empty">
        {f.system === "flashkut" ? <FkMark /> : <Icon name="live" />}
        <b>Nothing in <bdi>{name}</bdi> yet</b>
      </div>
    );
  }
  if (!canAdd) {
    return (
      <div className="as-empty">
        <Icon name="fold" />
        <b>Nothing in <bdi>{name}</bdi> yet</b>
      </div>
    );
  }
  return (
    <div className="as-empty as-empty-drop">
      <Icon name="upload" />
      <b>Nothing in <bdi>{name}</bdi> yet</b>
      <span>Drop files here, or</span>
      <button type="button" className="btn s p" onClick={onUpload}>
        <Icon name="upload" /> Upload
      </button>
    </div>
  );
}

interface ResultsProps extends GridProps {
  assets: LibraryAsset[];
  newFolder: boolean;
  sortControl?: ReactNode;
  onOpen: (id: string) => void;
  onToggleAll: () => void;
  onClear: () => void;
  onUpload: () => void;
  onCreateFolder: (name: string) => void;
  onCancelFolder: () => void;
  newFolderWhere: string;
  /** The actions of a folder the person may change: Rename, Move to, Delete. Left out for a viewer. */
  folderMenu?: (f: LibraryFolder) => ReactNode;
  canAdd?: boolean;
}

function menuOf(folders: LibraryFolder[], id: string, make?: (f: LibraryFolder) => ReactNode): ReactNode {
  const f = folders.find((x) => x.id === id);
  return f && make ? make(f) : null;
}

export function Results(p: ResultsProps) {
  const { ctx, folders, files } = p;
  const q = ctx.q.trim();
  const sub = q ? folderMatches(folders, q) : childFolders(folders, ctx.folder);
  return (
    <>
      <div className="as-gh">
        <Crumb ctx={ctx} folders={folders} menu={ctx.folder !== ALL && !q ? menuOf(folders, ctx.folder, p.folderMenu) : undefined} onOpen={p.onOpen} />
        <span className="as-ct num">{listedLine(folders, p.assets, ctx, files.length)}</span>
        {p.sortControl ? (
          <>
            <span className="sp" />
            {p.sortControl}
          </>
        ) : null}
      </div>
      <FitBar ctx={ctx} onToggle={p.onToggleAll} />
      {p.newFolder ? <NewFolderRow where={p.newFolderWhere} onCreate={p.onCreateFolder} onCancel={p.onCancelFolder} /> : null}
      {sub.length ? (
        <div className="as-fcards">
          {sub.map((f) => (
            <FolderCard
              key={f.id}
              f={f}
              count={countIn(folders, p.assets, f.id)}
              inName={q && f.parent_id ? folderName(folders, f.parent_id) : undefined}
              menu={p.folderMenu?.(f)}
              onOpen={() => p.onOpen(f.id)}
            />
          ))}
        </div>
      ) : null}
      {files.length ? (
        <FileGrid {...p} />
      ) : sub.length ? null : (
        <Empty ctx={ctx} folders={folders} assets={p.assets} onClear={p.onClear} onOpen={p.onOpen} onAll={p.onToggleAll} onUpload={p.onUpload} canAdd={p.canAdd} />
      )}
    </>
  );
}

/** Trash: the files deleted in the last 30 days, ticked to be restored. */
export function TrashResults({
  ctx,
  folders,
  files,
  ticked,
  ticks,
  onTick,
}: {
  ctx: ListContext;
  folders: LibraryFolder[];
  files: LibraryAsset[];
  ticked: ReadonlySet<string>;
  ticks: boolean;
  onTick: (id: string) => void;
}) {
  return (
    <>
      <div className="as-gh">
        <div className="as-crumb">
          <h2>Trash</h2>
        </div>
        <span className="as-ct num">
          {files.length} file{files.length === 1 ? "" : "s"}
        </span>
      </div>
      {files.length ? (
        <>
          <p className="as-tnote">{TRASH_NOTE}</p>
          <FileGrid ctx={ctx} folders={folders} files={files} uploads={[]} sel={null} auto={null} fresh={[]} height={164} onSelect={onTick} trash={ticked} ticks={ticks} />
        </>
      ) : (
        <div className="as-empty">
          <Icon name="trash" />
          <b>Trash is empty</b>
          <span>{TRASH_NOTE}</span>
        </div>
      )}
    </>
  );
}

/** A file shown big: the picture, or the video ready to play. */
export function Stage({ a, big = false }: { a: LibraryAsset; big?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const ar = aspectOf(a);
  const box: React.CSSProperties =
    ar < 1 ? { height: "var(--sh)", aspectRatio: ar.toFixed(3) } : ar === 1 ? { width: "min(100%,var(--sh))", aspectRatio: "1" } : { width: "100%", aspectRatio: ar.toFixed(3) };
  const canPlay = (a.kind === "video" || a.kind === "sound") && !!a.url && a.ready;
  return (
    <div className={`as-stage${big ? " big" : ""}`}>
      <div className="as-frame" style={box}>
        {playing && a.url ? (
          a.kind === "sound" ? (
            <audio src={a.url} controls autoPlay />
          ) : (
            <video src={a.url} controls autoPlay playsInline />
          )
        ) : (
          <>
            <Media a={a} />
            {canPlay ? (
              <button type="button" className="as-play" aria-label="Play" onClick={() => setPlaying(true)}>
                <Icon name="asplay" />
              </button>
            ) : null}
            {a.duration_sec != null ? <span className="as-dur num">{lenLabel(a.duration_sec)}</span> : null}
          </>
        )}
      </div>
    </div>
  );
}

export function ByLine({ a, fresh }: { a: LibraryAsset; fresh: boolean }) {
  if (a.source === "flashkut") {
    return (
      <span className="as-tag fk">
        <FkMark />
        Made in Flashkut
      </span>
    );
  }
  if (a.source === "room") {
    return (
      <span className="as-tag">
        <Icon name="live" />
        {a.from_label ?? "Recorded in Flashroom"}
      </span>
    );
  }
  if (fresh) {
    return (
      <span className="as-tag">
        <Icon name="upload" />
        Uploaded by you
      </span>
    );
  }
  return null;
}

/** Shape in a word, from what is known of the picture. */
function shapeWord(a: LibraryAsset): string {
  const r = aspectOf(a);
  return r < 1 ? "Tall" : r > 1.15 ? "Wide" : "Square";
}

export function Facts({ a, folders }: { a: LibraryAsset; folders: LibraryFolder[] }) {
  const rows: [string, string][] = [];
  const product = groupOfAsset(a);
  if (product) {
    // Why fewer: a product's file is read only here, so what a person wants to know is how long, what shape, and where it came from.
    if (a.kind !== "image" && a.duration_sec != null) rows.push(["Length", lenLabel(a.duration_sec)]);
    if (a.kind !== "sound") rows.push(["Shape", shapeWord(a)]);
    rows.push(["Added", agoLabel(a.created_at)]);
    rows.push(["From", `${product} · ${a.from_label ?? folderName(folders, a.folder_id)}`]);
  } else {
    if (a.duration_sec != null) rows.push(["Length", lenLabel(a.duration_sec)]);
    if (a.width && a.height) rows.push(["Size", `${a.width} × ${a.height}`]);
    if (a.pages) rows.push([kindName(a) === "Slides" ? "Slides" : "Pages", String(a.pages)]);
    if (a.size_bytes) rows.push(["File", bytesLabel(a.size_bytes)]);
    rows.push(["Added", agoLabel(a.created_at)]);
    rows.push(["Folder", folderName(folders, a.folder_id)]);
  }
  return (
    <dl className="as-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const USE_ROW: Record<LibraryUse["kind"], [IconName, string]> = {
  chat: ["chat", "Shared in the chat"],
  post: ["file", "On their page"],
  room: ["play", "In the room"],
  offer: ["gift", "The offer’s download"],
  email: ["mail", "An email"],
  all: ["globe", "Your logo, everywhere"],
  flashkut: ["fold", "In a Flashkut project"],
};

/** Where to open a place a file is used. */
function hrefOfUse(u: LibraryUse): string {
  if (u.kind === "chat") return "/conversations";
  if (u.kind === "room" || u.kind === "offer") return `/rooms/${u.ref}`;
  if (u.kind === "email") return `/automations/${u.ref}`;
  if (u.kind === "all") return "/settings";
  if (u.kind === "flashkut") return "";
  return "/people";
}

/** What a use of a file is called, with its icon, and where it opens ("" for a place with no page here). */
export function useRowOf(u: LibraryUse): { icon: IconName; what: string; href: string } {
  const [icon, what] = USE_ROW[u.kind];
  return { icon, what, href: hrefOfUse(u) };
}

export function RefRow({ u, onGo }: { u: LibraryUse; onGo?: (href: string) => void }) {
  const { icon, what, href } = useRowOf(u);
  const inner = (
    <>
      <span className="as-ri-ic">
        <Icon name={icon} />
      </span>
      <span className="sp">
        <b>{u.label}</b>
        <span>{what}</span>
      </span>
    </>
  );
  if (!onGo || u.kind === "flashkut") return <div className="as-ref">{inner}</div>;
  return (
    <button type="button" className="as-ref" onClick={() => onGo(href)}>
      {inner}
      <Icon name="chev" />
    </button>
  );
}
